import React, { useContext, useEffect, useMemo, useState, useReducer } from 'react';
import styled from '@emotion/styled';
import { withTheme } from '@emotion/react';
import { createNotification, useNotification } from './notification';
import {
  organismInitialState,
  organismStatusReducer,
  useAccountData,
  useApi,
  useFeedData,
  useFeedFilter,
  useFeedTimer,
  useFilterPropSync,
  useLivefireItemTypes,
  useLivefireStatus,
  useSearchTermPropSync,
  useSocket,
} from './status';
import { defaultMenuConfig } from './menuItems';
import { MainComponent } from './components/MainComponent';
import { LivefireProps, ViewState } from './types';
import EnvironmentHelper from '@/classes/EnvironmentHelper/EnvironmentHelper';
import { book, heavy, colors, medium, regular, semiBold, mediumInter } from '@/styles';
import { livefireItemTypes, LivefireCardType } from '@/constants';
import { FeedItem, LivefireApi, MetroContext, setMetroValueIfDefault } from '@/classes';

const defaultCardTypes: LivefireCardType[] = [
  livefireItemTypes.click,
  livefireItemTypes.open,
  livefireItemTypes.sent,
  livefireItemTypes.videoWatched,
  livefireItemTypes.comment,
  livefireItemTypes.like,
  livefireItemTypes.replyWithVideo,
];

/**
 * Livefire
 */
export function Livefire({
  api = new LivefireApi(),
  appName = 'modulelibrary',
  authToken = null,
  cardTypes = defaultCardTypes,
  className,
  clientId = null,
  environment = null,
  filterState = {},
  menuConfig = defaultMenuConfig,
  notification = createNotification(),
  onEmptyStateClick = () => {},
  onErrorClick = () => {},
  onFilterStateChange = null,
  onMetricTriggered = null,
  onOpenUrlAction = () => {},
  onSearchTermChange = () => {},
  searchTerm = '',
  showFilterOptions = false,
  showHeader = true,
  subscriptionId = 'unknown',
  subscriptionStatus = 'unknown',
  updateInterval = 10 * 1000,
  userName = 'unknown',
}: LivefireProps) {
  const [organismState, dispatch] = useReducer(organismStatusReducer, organismInitialState);

  const [livefireStatus, setLivefireStatus] = useState({
    api,
    appName,
    authToken,
    cardTypes,
    clientId,
    subscriptionId,
    subscriptionStatus,
    userName,
  });

  const inheritedMetroContext = useContext(MetroContext);
  const metroContext = useMemo(() => {
    const context = { ...inheritedMetroContext };
    setMetroValueIfDefault(context, 'appName', appName);
    const metroClientId = livefireStatus.clientId ? livefireStatus.clientId : clientId;
    if (metroClientId) {
      setMetroValueIfDefault(context, 'clientId', metroClientId);
    }
    setMetroValueIfDefault(
      context,
      'subscriptionId',
      livefireStatus.subscriptionId ?? subscriptionId,
    );
    setMetroValueIfDefault(
      context,
      'subscriptionStatus',
      livefireStatus.subscriptionStatus ?? subscriptionStatus,
    );
    setMetroValueIfDefault(context, 'userName', livefireStatus.userName ?? userName);
    setMetroValueIfDefault(context, 'customHandler', onMetricTriggered);
    return context;
  }, [
    appName,
    clientId,
    inheritedMetroContext,
    livefireStatus.clientId,
    livefireStatus.subscriptionId,
    livefireStatus.subscriptionStatus,
    livefireStatus.userName,
    onMetricTriggered,
    subscriptionId,
    subscriptionStatus,
    userName,
  ]);

  const [feedItems, setFeedItems] = useState([] as FeedItem[]);
  const [viewState, setViewState] = useState({
    filterButtonActive: false,
    filtered: false,
    filteredItems: [],
    itemTypeFilterEmptiedList: false,
    searchTerm: '',
    showHeader,
    selectedItemTypes: {
      [livefireItemTypes.open]: true,
      [livefireItemTypes.click]: true,
      [livefireItemTypes.videoWatched]: true,
      [livefireItemTypes.sent]: true,
      [livefireItemTypes.comment]: true,
      [livefireItemTypes.like]: true,
      [livefireItemTypes.replyWithVideo]: true,
      ...filterState,
    },
    showFilterOptions,
  } as ViewState);

  const [notificationState, setNotification] = useState(notification);
  const notificationBanner = useNotification(notificationState, setNotification);

  if (environment !== null) {
    EnvironmentHelper.setEnvironment(environment);
  }

  // sync props to view state as needed.
  useEffect(() => setViewState((state) => ({ ...state, showHeader })), [showHeader]);
  useEffect(() => setViewState((state) => ({ ...state, showFilterOptions })), [showFilterOptions]);

  useApi(livefireStatus, setLivefireStatus);
  useLivefireItemTypes(livefireStatus);
  useLivefireStatus(livefireStatus, setLivefireStatus, organismState, dispatch);
  useAccountData(livefireStatus, setLivefireStatus, organismState, dispatch);
  useFeedData(livefireStatus, setLivefireStatus, organismState, dispatch, setFeedItems);
  useFeedTimer(organismState, setFeedItems, updateInterval);
  useSocket(livefireStatus, organismState, setFeedItems, setNotification, createNotification);
  useFeedFilter(viewState, feedItems, organismState, setViewState);
  useSearchTermPropSync(searchTerm, viewState, onSearchTermChange, setViewState);
  useFilterPropSync(filterState, viewState, onFilterStateChange, setViewState);

  const callbacks = useMemo(
    () => ({ onEmptyStateClick, onErrorClick, onMetricTriggered, onOpenUrlAction }),
    [onEmptyStateClick, onErrorClick, onMetricTriggered, onOpenUrlAction],
  );

  return (
    <MetroContext.Provider value={metroContext}>
      <div className={`${className} ${notificationBanner ? 'has-overlay' : ''}`} role="feed">
        {notificationBanner && (
          <div className="notification">
            {notificationBanner}
            <div className="overlay" />
          </div>
        )}
        <MainComponent
          organismState={organismState}
          livefireStatus={livefireStatus}
          viewState={viewState}
          feedItems={feedItems}
          callbacks={callbacks}
          menuConfig={menuConfig}
          dispatch={dispatch}
          setViewState={setViewState}
        />
      </div>
    </MetroContext.Provider>
  );
}

const styledLivefire = styled(Livefire)`
  ${({ theme }) => (theme.isRebrand ? regular() : book())}
  position: relative;
  height: 100%;
  background-color: ${colors.fog};

  .livefire-header {
    padding: ${12 / 16}rem ${13 / 16}rem;
    background-color: ${colors.white};
    box-shadow: 0 0.1875rem 0.375rem ${colors.boxShadowLight};
    z-index: 50;
    display: flex;

    .searchbar {
      margin-right: ${10 / 16}rem;
      flex-grow: 2;
    }

    .filters {
      flex-basis: ${77 / 16}rem;
      background: transparent;
      color: ${colors.silverSandAlt};
      font-size: ${12 / 16}rem;
      z-index: 82;
      padding: 0;

      svg path {
        fill: ${colors.silverSandAlt};
      }

      &:hover,
      &:focus {
        color: ${colors.boulder};
        background: ${colors.fog};

        svg path {
          fill: ${colors.boulder};
        }
      }

      &.active,
      &.active:hover,
      &.active:focus {
        color: ${colors.taosSky};
        background: ${colors.taosSkyLightest};

        svg path {
          fill: ${colors.taosSky};
        }
      }
    }
  }

  .livefire-contents {
    flex-direction: column;
    display: flex;
    height: 100%;
    position: relative;

    .feed {
      overflow-y: auto;
      height: 100%;
      position: relative;
    }

    .feed.position--top.first-tooltip--true {
      overflow-y: visible;
    }
  }

  &.has-overlay {
    .livefire-contents .feed {
      overflow: hidden;
    }
  }

  .loading {
    position: relative;
    height: 100%;
    display: flex;
    align-content: center;
    justify-content: center;
  }

  .loading .dots {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .empty-state-container {
    height: 100%;
    background-color: ${colors.white};
  }

  .error-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
    padding-bottom: ${64 / 16}rem; /* perceived vertical centering */

    h1,
    .body {
      text-align: center;
    }

    &.too-filtered .body {
      max-width: 17rem;
      line-height: ${22 / 16}rem;
    }

    .strong {
      ${({ theme }) => (theme.isRebrand ? semiBold() : heavy())}
    }

    .no-results {
      align-items: center;
      background-color: ${colors.silverSand};
      border-radius: 0.25rem;
      display: flex;
      height: 2.125rem;
      justify-content: center;
      margin: 0 auto;
      width: 2.125rem;
    }
  }

  .feed-container {
    padding: ${12 / 16}rem ${13 / 16}rem;
  }

  .notification {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 100;

    .overlay {
      position: absolute;
      width: 100%;
      height: 100%;
      background: ${colors.white};
      opacity: 0.7;
    }
  }

  .livefire-submenu-behind {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 80;
  }

  .livefire-filter-list {
    position: absolute;
    top: ${52 / 16}rem;
    right: ${14 / 16}rem;
    z-index: 100;
    background-color: white;
    min-width: ${126 / 16}rem;
    box-shadow: 0 ${3 / 16}rem ${6 / 16}rem ${colors.boxShadow};
    z-index: 85;
  }

  .livefire-filter-item {
    ${({ theme }) => (theme.isRebrand ? mediumInter() : medium())}
    font-size: ${12 / 16}rem;
    color: ${colors.boulder};
    padding: ${5 / 16}rem ${11 / 16}rem;
    text-align: left;

    &:hover {
      background-color: ${colors.taosSkyLightest};
    }

    &:focus {
      box-shadow: inset 0 0 ${2 / 16}rem ${colors.taosSky};
    }

    svg {
      margin-top: -${3 / 16}rem;
      margin-right: ${6.82 / 16}rem;
      vertical-align: middle;
    }
  }
`;

export default withTheme(styledLivefire);
